import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CloudUpload, CloudDownload, Clock } from 'lucide-react';
import { toast } from 'sonner';


export default function BackupRestore() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      const response = await fetch(`${API}/data/backups`, {
        credentials: 'include'
      });
      if (response.ok) {
        setBackups(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    }
  };

  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/data/backup`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Backup created successfully!');
        fetchBackups();
      }
    } catch (error) {
      toast.error('Backup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (backupId) => {
    if (!confirm('Are you sure you want to restore from this backup? Current data will be replaced.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/data/restore/${backupId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Restore initiated. Contact support to complete.');
      }
    } catch (error) {
      toast.error('Restore failed');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold mb-8">Backup & Restore</h1>

      <Card className="p-8 glass-card text-center mb-6">
        <CloudUpload className="h-16 w-16 mx-auto mb-4 text-primary" />
        <h2 className="text-2xl font-bold mb-4">Create Backup</h2>
        <p className="text-muted-foreground mb-6">
          Backup all your data including vials, logs, schedules, and protocols
        </p>
        <Button onClick={handleBackup} disabled={loading} size="lg">
          {loading ? 'Creating Backup...' : 'Create Backup Now'}
        </Button>
      </Card>

      <Card className="p-6 glass-card">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <CloudDownload className="h-5 w-5" />
          Available Backups
        </h3>

        {backups.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No backups yet. Create your first backup above.
          </p>
        ) : (
          <div className="space-y-3">
            {backups.map((backup) => (
              <div
                key={backup.backup_id}
                className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border"
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {new Date(backup.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Backup ID: {backup.backup_id}
                  </p>
                </div>
                <Button
                  onClick={() => handleRestore(backup.backup_id)}
                  variant="outline"
                  size="sm"
                >
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mt-6 p-4 glass-card bg-info/10 border-info/30">
        <p className="text-info text-sm">
          💾 Backups are stored securely and retained for 90 days. For restore assistance, contact support@peptideforge.com
        </p>
      </Card>
    </div>
  );
}
